import { cn } from "~/lib/utils";
import { TabSelectItem } from "./types";
import { useState } from "react";
import { ClassnamePassthruWrapper } from "~/components/ui/classname-passthru-wrapper";
import { Icon } from "../Icon";
import { isValidIconType } from "../Icon";
import { ImageLoader } from "../ImageLoader";
import { useUser } from "~/modules/user/hooks/useUser";
import { faLock } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { RWA_COIN_ICON } from "~/consts";

// // // //

function getTabClassName(props: {
    tabStyle: "button" | "tab";
    active: boolean;
}) {
    const { active, tabStyle } = props;

    if (tabStyle === "button") {
        return cn(
            "rounded-md px-3 transition-colors text-xs py-1 border border-blue-900/10 select-none", // Base button styles.
            {
                "bg-primary text-white cursor-default": active,
                "bg-blue-100 text-link hover:bg-blue-200 shadow-xs": !active,
            },
        );
    }

    return cn(
        "whitespace-nowrap border-b-2 py-2.5 text-sm font-medium flex grow justify-center items-center gap-1.5 transition-all",
        {
            "border-primary text-primary": active,
            "border-transparent text-secondary-foreground/80 hover:border-slate-700 hover:text-foreground":
                !active,
        },
    );
}

/**
 * Renders a selector for changing between different tabs
 */
export function TabSelectControlled<T>(props: {
    tabs: TabSelectItem<T>[];
    activeTab: TabSelectItem<T>;
    onSelectTab: (selected: TabSelectItem<T>) => void;
    children: (tab: TabSelectItem<T>) => React.ReactNode;
    name: string;
    tabStyle?: "button" | "tab";
    className?: string;
    isLoggedIn: boolean;
    onTabChange?: (params: {
        prev: TabSelectItem<T>;
        next: TabSelectItem<T>;
    }) => void;
}) {
    const {
        tabs,
        activeTab,
        onSelectTab,
        name,
        tabStyle = "button",
        className = null,
        onTabChange = null,
        isLoggedIn,
    } = props;

    // Do not render the component if there are less than two modes to switch between.
    if (tabs.length < 2) {
        return <>{props.children(activeTab)}</>;
    }

    // Function to invoke setActiveTab and onTabChange (if available)
    function setActiveTab(updatedTab: TabSelectItem<T>) {
        const prev = activeTab;
        const next = updatedTab;

        // Invoke  props.onTabChange
        if (onTabChange !== null) {
            onTabChange({ prev, next });
        }

        // Set the updated tab state
        onSelectTab(updatedTab);
    }

    // Render a button for each group
    return (
        <>
            <ClassnamePassthruWrapper className={className}>
                <TabSelectMobile
                    {...props}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    name={name}
                />
                <div
                    className={cn("hidden lg:flex", {
                        "gap-2 mt-1 flex-wrap": tabStyle === "button",
                        "border-b border-t": tabStyle === "tab",
                    })}
                >
                    <ClassnamePassthruWrapper
                        className={
                            tabStyle === "button"
                                ? null
                                : "flex grow w-full justify-between"
                        }
                    >
                        {tabs.map((group) => (
                            <button
                                key={group.key}
                                data-testid={group.testID}
                                onClick={() => {
                                    if (group.key === activeTab.key) return;
                                    setActiveTab(group);
                                }}
                                className={getTabClassName({
                                    tabStyle,
                                    active: group.key === activeTab.key,
                                })}
                            >
                                {group.content.icon &&
                                    isValidIconType(group.content.icon) && (
                                        <Icon
                                            icon={group.content.icon}
                                            className="h-3"
                                        />
                                    )}

                                {group.content.icon &&
                                    !isValidIconType(group.content.icon) && (
                                        <ImageLoader
                                            src={group.content.icon}
                                            fallbackSrc={RWA_COIN_ICON}
                                            className="h-4 rounded-full"
                                            alt={`${group.content.label} Icon`}
                                        />
                                    )}

                                {/* Login required indicator */}
                                {group.loginRequired && !isLoggedIn && (
                                    <FontAwesomeIcon
                                        icon={faLock}
                                        className="h-2.5 w-2.5"
                                    />
                                )}

                                {group.content.label}
                            </button>
                        ))}
                    </ClassnamePassthruWrapper>
                </div>
            </ClassnamePassthruWrapper>

            <>{props.children(activeTab)}</>
        </>
    );
}

/**
 * Renders a selector for changing between different tabs
 */
export function TabSelect<T>(props: {
    tabs: TabSelectItem<T>[];
    children: (tab: TabSelectItem<T>) => React.ReactNode;
    name: string;
    tabStyle?: "button" | "tab";
    className?: string;
    onTabChange?: (params: {
        prev: TabSelectItem<T>;
        next: TabSelectItem<T>;
    }) => void;
    onTabChangeLoginRequired?: (params: {
        prev: TabSelectItem<T>;
        next: TabSelectItem<T>;
    }) => void;
}) {
    const { tabs, onTabChangeLoginRequired = null } = props;
    const { isLoggedIn } = useUser();

    // State to keep track of the active tab index
    const [activeTabIndex, setActiveTabIndex] = useState(0);

    // `tabs` can shrink while this instance is mounted (e.g. index 3 with
    // only 2 tabs left). Snap the stored index into range during render so
    // React retries with a valid selection instead of reading `undefined`.
    const safeActiveTabIndex =
        tabs.length === 0
            ? 0
            : Math.max(0, Math.min(activeTabIndex, tabs.length - 1));

    if (safeActiveTabIndex !== activeTabIndex) {
        setActiveTabIndex(safeActiveTabIndex);
    }

    const activeTab = tabs[safeActiveTabIndex];

    // Function to invoke setActiveTab and onTabChange (if available)
    const onSelectTab = (selectedTab: TabSelectItem<T>) => {
        // Invoke props.onTabChangeLoginRequired if
        // TabSelectItem.loginRequired is true and the user isn't logged in
        if (selectedTab.loginRequired && !isLoggedIn) {
            if (onTabChangeLoginRequired !== null) {
                onTabChangeLoginRequired({
                    prev: activeTab,
                    next: selectedTab,
                });
            }
            // Stop execution here
            return;
        }

        const newIndex = tabs.findIndex((tab) => tab.key === selectedTab.key);
        if (newIndex !== -1) {
            setActiveTabIndex(newIndex);
        }
    };

    return (
        <TabSelectControlled
            activeTab={activeTab}
            onSelectTab={onSelectTab}
            isLoggedIn={isLoggedIn}
            {...props}
        />
    );
}

/**
 * TabSelectMobile
 * Mobile-only selector UI. Exported for reuse by VerticalTabSelect.
 */
export function TabSelectMobile<T>(props: {
    activeTab: TabSelectItem<T>;
    tabs: TabSelectItem<T>[];
    setActiveTab: (updated: TabSelectItem<T>) => void;
    isLoggedIn: boolean;
    name: string;
    className?: string;
}) {
    const { activeTab, tabs, setActiveTab, isLoggedIn, name, className } =
        props;

    return (
        <div className={cn("lg:hidden", className)}>
            <label htmlFor={name} className="sr-only">
                Select a tab
            </label>
            {/* ENHANCEMENT - use our standard SelectInput here */}
            <select
                id={name}
                name={name}
                value={activeTab.key}
                className="block w-full border-border rounded-md py-1 pl-3 mt-1 mb-2 pr-10 text-sm text-slate-900 focus:border-blue-500 focus:outline-hidden focus:ring-blue-500 sm:text-sm"
                onChange={(e) => {
                    const modeKey = e.currentTarget.value;
                    const selectedMode = tabs.find((m) => m.key === modeKey);
                    if (!selectedMode) {
                        return;
                    }
                    setActiveTab(selectedMode);
                }}
            >
                {tabs.map((group) => (
                    <option
                        key={group.key}
                        value={group.key}
                        disabled={group.loginRequired && !isLoggedIn}
                    >
                        {group.content.label}
                        {group.loginRequired &&
                            !isLoggedIn &&
                            ` (Login Required)`}
                    </option>
                ))}
            </select>
        </div>
    );
}
